import React, { useState } from "react";

type Props = {
    onStart: (avatarImage: string, groupName: string) => void;
};

export default function WelcomeScreen({ onStart }: Props) {
    // New step order:
    // 0: Group Name Input
    // 1: Avatar Selection
    // 2: Intro Messages with selected Avatar
    const [currentStep, setCurrentStep] = useState(0);
    const [currentIntroMessageIndex, setCurrentIntroMessageIndex] = useState(0);
    const [selectedAnimal, setSelectedAnimal] = useState<string | null>(null);
    const [selectedColor, setSelectedColor] = useState<string | null>(null);
    const [avatarImage, setAvatarImage] = useState<string | null>(null);
    const [groupName, setGroupName] = useState<string>("");

    // Intro messages based on the screenshots
    const introMessages = [
        {
            text: (groupName: string) => `Hallo ${groupName ? groupName + "," : ""} ich heiße KIKI! Ich freue mich, euch kennenzulernen. ☺️`,
        },
        {
            text: () => "Ich bin eine Künstliche Intelligenz – oder kurz: KI. Ich bin so etwas wie ein schlaues Computerprogramm. 💻",
        },
        {
            text: () => "Ich kann zum Beispiel lernen, Dinge auf Bildern zu erkennen. Aber damit ich das kann, muss man mich zuerst trainieren – kannst du mir dabei helfen?",
        },
        {
            text: () => "Wenn du dabei bist, klicke 'Weiter'!",
        },
    ];

    // Image paths now correctly point to the /icons/ folder
    const animals = [
        { name: "dino", value: "dino" },
        { name: "koala", value: "koala" },
        // Add more animals as needed
    ];

    const colors = [
        { name: "Gelb", value: "gelb", code: "#FFD700" },
        { name: "Grün", value: "grün", code: "#32CD32" },
        { name: "Orange", value: "orange", code: "#FFA500" },
        { name: "Braun", value: "braun", code: "#A0522D" },
        { name: "Grau", value: "grau", code: "#808080" },
        // Füge hier weitere Farben hinzu
    ];

    const handleAnimalSelect = (animalValue: string) => {
        setSelectedAnimal(animalValue);
        setSelectedColor(null);
        setAvatarImage(null); // Reset avatar image until color is chosen
    };

    const handleColorSelect = (colorValue: string) => {
        setSelectedColor(colorValue);
        if (selectedAnimal) {
            // Path now correctly points to the /icons/ folder
            const imagePath = `/icons/${selectedAnimal}_${colorValue}.png`;
            setAvatarImage(imagePath);
        }
    };

    const handleGroupNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGroupName(event.target.value);
    };

    const handleStartGame = () => {
        if (avatarImage && groupName.trim() !== "") {
            onStart(avatarImage, groupName);
        } else {
            alert("Bitte wähle zuerst ein Tier und eine Farbe und gib einen Gruppennamen ein!");
        }
    };

    const handleNextIntroMessage = () => {
        if (currentIntroMessageIndex < introMessages.length - 1) {
            setCurrentIntroMessageIndex(currentIntroMessageIndex + 1);
        } else {
            // After all intro messages, proceed to start the game
            handleStartGame(); // Call handleStartGame directly
        }
    };

    const renderGroupNameInputScreen = () => (
        <div
            style={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                padding: "40px",
                borderRadius: "15px",
                boxShadow: "0 0 20px rgba(0,0,0,0.2)",
                maxWidth: "600px",
                width: "90%",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <h1 style={{ fontSize: '2.5em', fontWeight: '800', color: '#333', marginBottom: '20px' }}>
                Willkommen bei Klassifix!
            </h1>
            <p style={{ fontSize: '1.2em', color: '#555', marginBottom: '30px', lineHeight: '1.6' }}>
                Gib deinen Gruppennamen ein, um zu starten.
            </p>

            <div style={{ marginBottom: '30px' }}>
                <input
                    type="text"
                    value={groupName}
                    onChange={handleGroupNameChange}
                    placeholder="Gruppenname hier eingeben"
                    style={{
                        padding: '12px',
                        fontSize: '1.2em',
                        borderRadius: '8px',
                        border: '2px solid #ccc',
                        width: '80%',
                        maxWidth: '300px',
                        textAlign: 'center',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                    }}
                />
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                marginTop: 30,
            }}>
                <button
                    onClick={() => {
                        if (groupName.trim() !== "") {
                            setCurrentStep(1);
                        } else {
                            alert("Bitte gib einen Gruppennamen ein!");
                        }
                    }}
                    disabled={groupName.trim() === ""}
                    style={{
                        fontSize: '1.5em',
                        padding: "15px 30px",
                        borderRadius: "12px",
                        border: "none",
                        backgroundColor: groupName.trim() !== "" ? "#007bff" : "#ccc",
                        color: "white",
                        cursor: groupName.trim() !== "" ? "pointer" : "not-allowed",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                        transition: 'background-color 0.3s ease',
                        outline: 'none',
                    }}
                >
                    Weiter
                </button>
            </div>
        </div>
    );

    const renderAvatarSelectionScreen = () => (
        <div
            style={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                padding: "40px",
                borderRadius: "15px",
                boxShadow: "0 0 20px rgba(0,0,0,0.2)",
                maxWidth: "600px",
                width: "90%",
                textAlign: "center",
            }}
        >
            <h2 style={{ fontSize: '2.5em', fontWeight: '800', color: '#333', marginBottom: '30px' }}>Wähle deinen Avatar</h2>

            <div style={{ marginBottom: '30px' }}>
                <h3 style={{ fontSize: '1.8em', fontWeight: '600', color: '#555', marginBottom: '15px' }}>Schritt 1: Wähle ein Tier</h3>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                    {animals.map((animal) => (
                        <button
                            key={animal.value}
                            onClick={() => handleAnimalSelect(animal.value)}
                            style={{
                                padding: '15px',
                                border: `3px solid ${selectedAnimal === animal.value ? '#4caf50' : '#ccc'}`,
                                borderRadius: '12px',
                                cursor: 'pointer',
                                backgroundColor: selectedAnimal === animal.value ? '#e0f7fa' : 'white',
                                transition: 'background-color 0.3s ease, border-color 0.3s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                outline: 'none',
                            }}
                        >
                            <img
                                src={
                                    selectedColor
                                        ? `/icons/${animal.value}_${selectedColor}.png`
                                        : `/icons/${animal.value}_gelb.png`
                                }
                                alt={`${animal.name} Icon`}
                                style={{ width: 70, height: 70, marginBottom: '8px' }}
                            />
                            <span style={{ fontSize: '1.2em', color: '#333' }}>{animal.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {selectedAnimal && (
                <div style={{ marginBottom: '30px' }}>
                    <h3 style={{ fontSize: '1.8em', fontWeight: '600', color: '#555', marginBottom: '15px' }}>Schritt 2: Wähle eine Farbe</h3>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                        {colors.map((color) => (
                            <button
                                key={color.value}
                                onClick={() => handleColorSelect(color.value)}
                                style={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: '50%',
                                    backgroundColor: color.code,
                                    border: `3px solid ${selectedColor === color.value ? '#4caf50' : '#ccc'}`,
                                    cursor: 'pointer',
                                    opacity: selectedAnimal ? 1 : 0.5,
                                    transition: 'border-color 0.3s ease',
                                    outline: 'none',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                }}
                                disabled={!selectedAnimal}
                                aria-label={`Farbe ${color.name}`}
                            >
                                {selectedColor === color.value && <span style={{ fontSize: '1.5em' }}>✓</span>}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {avatarImage && (
                <div style={{ width: '150px', height: '150px', margin: '30px auto', borderRadius: '50%', overflow: 'hidden', boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
                    <img src={avatarImage} alt="Ausgewählter Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
            )}

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '10px',
                marginTop: 30,
            }}>
                <button
                    onClick={() => setCurrentStep(0)}
                    style={{
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '12px 25px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    Zurück
                </button>
                <button
                    onClick={() => {
                        if (avatarImage) {
                            setCurrentStep(2);
                            setCurrentIntroMessageIndex(0);
                        } else {
                            alert("Bitte wähle zuerst ein Tier und eine Farbe!");
                        }
                    }}
                    disabled={!avatarImage}
                    style={{
                        fontSize: '1.5em',
                        padding: "15px 30px",
                        borderRadius: "12px",
                        border: "none",
                        backgroundColor: avatarImage ? "#4caf50" : "#ccc",
                        color: "white",
                        cursor: avatarImage ? "pointer" : "not-allowed",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                        transition: 'background-color 0.3s ease',
                        outline: 'none',
                    }}
                >
                    Avatar auswählen
                </button>
            </div>
        </div>
    );

    const renderIntroMessagesScreen = () => {
        const currentMessage = introMessages[currentIntroMessageIndex];
        return (
            <div
                style={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    padding: "40px",
                    borderRadius: "15px",
                    boxShadow: "0 0 20px rgba(0,0,0,0.2)",
                    maxWidth: "600px",
                    width: "90%",
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: '300px', // Ensure consistent height
                }}
            >
                <h1 style={{ fontSize: '2.5em', fontWeight: '800', color: '#333', marginBottom: '20px' }}>
                    Willkommen bei Klassifix!
                </h1>

                {/* Display the SELECTED Avatar Image here */}
                {avatarImage && (
                    <div style={{ width: '120px', height: '120px', margin: '20px auto', borderRadius: '50%', overflow: 'hidden', boxShadow: '0 4px 8px rgba(0,0,0,0.2)', border: '3px solid #eee' }}>
                        <img
                            src={avatarImage} // Use the dynamically selected avatar image
                            alt="Your Avatar"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </div>
                )}


                <p style={{ fontSize: '1.2em', color: '#555', marginBottom: '30px', lineHeight: '1.6' }}>
                    {currentMessage.text(groupName)}
                </p>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    gap: '10px',
                    marginTop: '30px'
                }}>
                    <button
                        onClick={() => {
                            if (currentIntroMessageIndex > 0) {
                                setCurrentIntroMessageIndex(currentIntroMessageIndex - 1);
                            } else {
                                setCurrentStep(1); // Go back to avatar selection
                            }
                        }}
                        style={{
                            backgroundColor: '#f0f0f0',
                            color: '#555',
                            border: '1px solid #ccc',
                            borderRadius: '8px',
                            padding: '12px 25px',
                            cursor: (currentIntroMessageIndex === 0 && currentStep === 2) ? 'pointer' : 'pointer',
                            fontSize: '1.1em',
                            fontWeight: 600,
                        }}
                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                        onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    >
                        Zurück
                    </button>
                    <button
                        onClick={handleNextIntroMessage}
                        style={{
                            fontSize: '1.5em',
                            padding: "15px 30px",
                            borderRadius: "12px",
                            border: "none",
                            backgroundColor: "#007bff",
                            color: "white",
                            cursor: "pointer",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                            transition: 'background-color 0.3s ease',
                            outline: 'none',
                        }}
                    >
                        {currentIntroMessageIndex < introMessages.length - 1 ? "Weiter" : "Weiter"}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div
            style={{
                position: "relative",
                height: "100vh",
                width: "100vw",
                overflow: "hidden",
                fontFamily: 'Nunito, sans-serif',
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            {currentStep === 0 && renderGroupNameInputScreen()}
            {currentStep === 1 && renderAvatarSelectionScreen()}
            {currentStep === 2 && renderIntroMessagesScreen()}
        </div>
    );
}